import type { ImageUpload } from './ImageUpload'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { getUserId } from '../utils/userUtils'
import { useLogger } from '../../composables/useLogger'
import { AlbumImage, ImageBlob, ImageGame } from './index'
import { openDB } from 'idb'

const logger = useLogger()

export class Image {
    id: string | null;
    name: string | null;
    size: number | null;
    type: string | null;

    constructor() {
        this.id = null
        this.name = null
        this.size = null
        this.type = null
    }

    // static method to get the supabase client
    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore()
        return supabaseClientStore.supabase
    }

    static async fromDatabaseRow(row: any): Promise<Image> {
        const image = new Image()
        image.id = row.id
        image.name = row.name
        image.size = row.size
        image.type = row.type
        return image
    }

    /**
     * Get an image from the database. 
     * @param {string} id - The id of the image to get.
     * @returns {Promise<Image>} The image.
     */
    static async fromDatabase(id: string): Promise<Image> {
        logger.debug('97. Image data from relation:', { id: id })
        logger.debug('98.5. Getting image from database:', { id: id })

        const { data, error } = await Image.getSupabase()
            .from('images')
            .select('*')
            .eq('id', id)
            .single()
        if (error) logger.throwSupabaseError(error, `Failed to get image with ID ${id}`)
        logger.debug('99. Image data from database:', { data: data })
        const image = await Image.fromDatabaseRow(data)

        return image
    }

    /**
     * Create a new image from an upload.
     * @param {ImageUpload} imageUpload - The image to upload.
     * @returns {Promise<Image>} The new image.
     */
    static async createFromUpload(imageUpload: ImageUpload): Promise<Image> {
        const { data: newImageData, error: imageError } = await Image.getSupabase()
            .from('images')
            .insert({
                name: imageUpload.name,
                size: imageUpload.size,
                type: imageUpload.type
            })
            .select()
            .single()
        if (imageError) logger.throwSupabaseError(imageError, `Failed to create image from upload ${imageUpload.name}`);
        const newImage = await Image.fromDatabaseRow(newImageData)
        logger.debug('101. New image:', { newImage: newImage })

        const imageBlob = await ImageBlob.createFromUpload(imageUpload, newImage.id!)

        logger.debug('104. Final image with blob:', { image: newImage })
        return newImage
    }

    async getImageBlob(): Promise<ImageBlob> {
        const imageBlob = await ImageBlob.create(this.id!)
        await imageBlob.getBlobUrl()
        return imageBlob
    }

    /**
     * Delete an image and its associated data.
     * @param {string} imageId - The id of the image to delete.
     * @returns {Promise<void>}
     */
    static async deleteImage(imageId: string): Promise<void> {
        logger.debug('Deleting image:', { imageId: imageId })

        try {
            // Delete the image blob from Supabase storage
            const { error: storageError } = await Image.getSupabase()
                .storage
                .from('imageblobs')
                .remove([imageId])
            if (storageError) {
                logger.error('Error deleting image from Supabase storage:', storageError)
                logger.throwSupabaseError(storageError, `Failed to delete image blob ${imageId} from storage`)
            }

            // Delete from IndexedDB only if it's available (i.e., in a browser environment)
            if (typeof indexedDB !== 'undefined') {
                const db = await openDB('ImageStore', 2)
                await db.delete('images', imageId)
                await db.delete('metadata', imageId)
                db.close()
            } else {
                logger.debug('IndexedDB not available, skipping local deletion')
            }

            // Delete the image from the images table in Supabase
            const { error: dbError } = await Image.getSupabase()
                .from('images')
                .delete()
                .eq('id', imageId)
            if (dbError) {
                logger.error('Error deleting image from database:', dbError)
                logger.throwSupabaseError(dbError, `Failed to delete image ${imageId} from database`)
            }

            logger.debug('Image deleted successfully:', { imageId: imageId })
        } catch (error) {
            logger.logErrorCaught('Error during image deletion', 'Image.ts', error)
            logger.throwSupabaseError(error, `Failed to delete image ${imageId}`)
        }
    }
}